import React, { useState } from "react";
import styled from "styled-components";
import { Repeater, types } from "react-bricks/frontend";
import { mobile, tablet } from "components/templates/Breakpoints";
import { color } from "styles";
import { TestimonialItemProps } from "./TestimonialItem";
import {
  getVerticalPadding,
  VerticalPadding,
  VerticalPaddingWrapper,
  verticalPadSideProp,
} from "../utils/verticalPadding";

interface QuoteCarouselProps {
  testimonials: types.RepeaterItems<TestimonialItemProps>;
  paddingVertical: VerticalPadding;
}

const QuoteCarousel: types.Brick<QuoteCarouselProps> = ({ testimonials, paddingVertical }) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [opacity, setOpacity] = useState(1);
  const [translateX, setTranslateX] = useState("0");
  const [transitionSpeed, setTransitionSpeed] = useState("400ms");

  const handleClick = (newSlide: number) => {
    if (newSlide === activeIndex) return;

    setTransitionSpeed("400ms");
    setOpacity(0);
    setTranslateX("-20%");

    setTimeout(() => {
      setTransitionSpeed("250ms");
      setTranslateX("20%");
    }, 400);

    setTimeout(() => {
      setTranslateX("0");
      setOpacity(1);
      setActiveIndex(newSlide);
    }, 650);
  };

  return (
    <VerticalPaddingWrapper paddingVertical={paddingVertical}>
      <Wrapper>
        <Transition $opacity={opacity} $translateX={translateX} $transitionSpeed={transitionSpeed}>
          <Repeater
            propName="testimonials"
            items={testimonials}
            itemProps={{
              activeIndex,
              setActiveIndex,
            }}
          />
        </Transition>
        <CircleWrapper>
          {testimonials &&
            testimonials.length > 1 &&
            testimonials.map((_, i) => (
              <Circle
                key={`circle-${i}`}
                onClick={() => handleClick(i)}
                $isSelected={i === activeIndex}
              />
            ))}
        </CircleWrapper>
      </Wrapper>
    </VerticalPaddingWrapper>
  );
};

QuoteCarousel.schema = {
  name: "quote-carousel",
  label: "Quote Carousel",
  category: "Testimonial Components",
  previewImageUrl: "/static/bricks/quote-carousel.png",
  // Define repeaters for child bricks
  repeaterItems: [
    {
      name: "testimonials",
      itemType: "testimonial-item",
      itemLabel: "Testimonial",
      min: 1,
      max: 10,
    },
  ],

  // Define default props
  getDefaultProps: () => ({
    paddingVertical: "none",
    testimonials: [
      {
        quote:
          "I haven’t had a single employee not engage with the app. Everyone is walking to work to gain YuCoin!",
        source: "Sol Zygadlo, Vodafone Partner Agent and Director, Vodafone",
        caseStudyURL:
          "https://yulife.com/case-studies/yulife-inspires-healthy-performance-among-employees-at-vodafone-franchise-stores/",
      },
      {
        quote:
          "As soon as we launched it, our notifications went crazy! Everyone was counting their steps and talking about YuLife.",
        source: "Alisha Cohen, People Director, Vanti",
        avatar: { src: "/static/img/carousel/Alisha-Cohen.png" },
        vimeoId: "679283779",
      },
      {
        quote:
          "I’ve had so many comments from managers telling me how motivated and engaged their teams are. All I've heard this morning is people comparing step challenges!",
        source: "Clare Jones, Head of HR at Fladgate",
        avatar: { src: "/static/pages/rewards/clare-fladgate.png" },
        vimeoId: "362316499",
      },
    ],
  }),

  sideEditProps: [verticalPadSideProp],
};

export default QuoteCarousel;

const Wrapper = styled.div`
  background: ${color.S3100};
  position: relative;
`;

const Transition = styled.div<{ $opacity: number; $translateX: string; $transitionSpeed: string }>`
  opacity: ${(p) => p.$opacity};
  translate: ${(p) => p.$translateX} 0;
  transition: ${(p) => p.$transitionSpeed};
`;

const Circle = styled.button<{ $isSelected?: boolean }>`
  border-radius: 50%;
  height: 1rem;
  width: 1rem;
  padding: 0;
  border: none;
  background-color: ${(p) => (p.$isSelected ? color.white : color.S350)};
  cursor: pointer;
`;

const CircleWrapper = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  padding-top: 2.5rem;
  padding-bottom: 2rem;

  ${mobile} {
    padding-top: 1rem;
  }
`;
